import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { MsalProvider } from '@azure/msal-react'
import { EventType, type AuthenticationResult } from '@azure/msal-browser'
import { loadConfig } from './config'
import { buildMsalInstance } from './auth/msal'
import { App } from './App'

// ATC-109 — the design layer, imported but NOT YET APPLIED.
//
// The console must render byte-for-byte identically to master at the end of
// ATC-109; `ATC-110` is the flip. These four imports are safe because:
//
//  • the fontsource packages only register `@font-face` rules for
//    'Geist Variable' / 'Geist Mono Variable', and nothing sets `font-family`
//    to either yet — `styles.css` still declares `system-ui` on `:root`;
//  • `styles/tokens.css` declares only `--rx-*` custom properties, so it cannot
//    shadow the unprefixed `--accent` / `--bg` / `--text` set `styles.css` owns;
//  • `styles/primitives.css` scopes every selector under an `.rx-` class, and
//    nothing in the app carries one yet.
//
// `styles.css` is imported LAST and remains authoritative for the live
// appearance. Do not reorder these above it.
import '@fontsource-variable/geist'
import '@fontsource-variable/geist-mono'
import './styles/tokens.css'
import './styles/primitives.css'
import './styles.css'

async function bootstrap() {
  const root = createRoot(document.getElementById('root')!)
  try {
    const config = await loadConfig()
    const msal = buildMsalInstance(config)
    await msal.initialize()

    // Handle the redirect response and set the active account.
    await msal.handleRedirectPromise()
    const accounts = msal.getAllAccounts()
    if (accounts.length > 0) {
      msal.setActiveAccount(accounts[0])
    }
    msal.addEventCallback((event) => {
      if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
        msal.setActiveAccount((event.payload as AuthenticationResult).account)
      }
    })

    root.render(
      <StrictMode>
        <MsalProvider instance={msal}>
          <BrowserRouter>
            <App config={config} />
          </BrowserRouter>
        </MsalProvider>
      </StrictMode>,
    )
  } catch (err) {
    root.render(
      // ATC-136: NOT migrated to `Notice`, deliberately. This renders when
      // config bootstrap FAILS — before the app tree the primitives live in
      // exists — so importing a component here would put a second failure mode
      // inside the handler for the first.
      <div className="notice notice-error" style={{ margin: '2rem' }}>
        Failed to start the Operator Console: {err instanceof Error ? err.message : String(err)}
      </div>,
    )
  }
}

void bootstrap()
